import { useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import type { OutreachEditorRef } from '@recruit-robin/robin-theme/web-components';
import type { CandidateRequirementsDTO, QuestionnaireRequirements } from 'clients/Communication/Questionnaire.dto';
import type { Candidate } from 'model';

import { Vacancy } from '@/clients/AutoCompleteClient/types';
import { communicationClient } from 'clients/Communication/Communication';
import { candidateRequirementsToQuestionnaireRequirements } from 'clients/Communication/questionnaireConfigurationMappers';
import { getNylasInterviewType } from '@/features/nylas-calendar-manage-connection-modal/lib';
import { candidateHasEmail } from '@/utils/candidates';
import { buildRjWhatsappJobDetails } from '@/shared/lib/rj-whatsapp-message';
import { CandidateContext } from 'shared/legacy/contexts/CandidateContext/CandidateContext';
import { useIsIndeedFlow, useIsWhatsAppOnlyFlow } from 'shared/legacy/contexts/CommAgentFlowContext';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { resolveIndeedRjSendErrorMessage } from 'shared/legacy/exceptions/resolveIndeedRjSendErrorMessage';
import useFeatureToggles, { FeatureToggleType } from 'shared/legacy/hooks/useFeatureToggles';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { jobBoardLabel } from 'helpers/jobBoards';
import { useWhatsappPreviewMessage } from './RJCommunicationHub.useWhatsappPreviewMessage';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { ActivateRobinJrDialogType } from './ActivateRobinJrDialog/ActivateRobinJrDialog.types';
import { ModalState } from './RJCommunicationHub.types';
import { useModalNavigation } from './RJCommunicationHub.useModalNavigation';
import { useOperations } from './RJCommunicationHub.useOperations';
import { useUserFeatureToggles } from './RJCommunicationHub.useUserFeatureToggles';
import {
  getQuestionnaireConfigurationQueryKey,
  questionnaireConfigurationQueryFn,
  useQuestionnaireConfigurationQuery,
} from './useQuestionnaireConfiguration';
import { useIndeedCommFlow } from './useIndeedCommFlow';

const useSetupInitialVacancy = (
  initialVacancy: Vacancy | undefined,
  {
    operations,
    handleConfirmSelectedVacancy,
  }: {
    operations: ReturnType<typeof useOperations>;
    handleConfirmSelectedVacancy: () => Promise<void>;
  },
) => {
  // Track which vacancy ID we've already processed to avoid duplicate calls
  const processedVacancyIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!initialVacancy?.db_id) {
      processedVacancyIdRef.current = null;
      return;
    }

    // If we've already processed this vacancy, skip
    if (processedVacancyIdRef.current === initialVacancy.db_id) {
      return;
    }

    // Vacancy is already set via initial state in useOperations
    // Modal state is already set to LOADING_GENERATED_MESSAGE in useModalNavigation
    // Now we just need to trigger the confirmation flow after query completes

    // Wait for rjActivityLogsQuery to complete if needed for resuming check
    if (operations.rjActivityLogsQuery.isLoading) {
      return;
    }

    // Query is loaded, proceed with confirmation flow
    processedVacancyIdRef.current = initialVacancy.db_id;
    handleConfirmSelectedVacancy();
  }, [initialVacancy?.db_id, operations.rjActivityLogsQuery.isLoading, handleConfirmSelectedVacancy]);
};

export const useContainer = ({ candidate, initialVacancy }: { candidate: Candidate; initialVacancy?: Vacancy }) => {
  const { close: handleOutreachModalRequestClose } = useGlobalDialog(GlobalDialogType.CandidateOutreach);
  const onRJComm = useContextSelector(CandidateContext, (state) => state.onRJComm);
  const isIndeedFlow = useIsIndeedFlow();
  const isWhatsAppOnlyFlow = useIsWhatsAppOnlyFlow();
  const { data: currentUser } = useCurrentUserQuery();
  const buildWhatsappMessage = useWhatsappPreviewMessage();
  const { createAttempt, sendIndeedOutreach, selectedIndeedProject, setSelectedIndeedProject } = useIndeedCommFlow();
  const { dictionary } = useContext(LocalizationContext);
  const { createSnackbar } = useContext(SnackbarContext);

  const [subject, setSubject] = useState<OutreachEditorRef | null>(null);
  const [message, setMessage] = useState<OutreachEditorRef | null>(null);

  const queryClient = useQueryClient();
  const featureToggles = useUserFeatureToggles();
  const { isFeatureToggleEnabled } = useFeatureToggles();
  const isWaCommunicationAgentEnabled = isFeatureToggleEnabled(FeatureToggleType.WA_COMMUNICATION_AGENT);
  const modalNavigation = useModalNavigation({
    candidate,
    initialVacancy,
    dontShowMessagePreview: featureToggles.dontShowMessagePreview,
    dontShowWhatsAppMessagePreview: featureToggles.dontShowWhatsAppMessagePreview,
    isWaCommunicationAgentEnabled,
  });
  const operations = useOperations({ candidate, initialVacancy });

  const questionnaireConfigJobId = operations.selectedVacancy?.db_id
    ? Number(operations.selectedVacancy.db_id)
    : undefined;
  const isPastVacancyConfirm =
    modalNavigation.modalState !== ModalState.ACTIVATE_ROBIN_JR && modalNavigation.modalState !== ModalState.NONE;
  const { data: questionnaireConfig } = useQuestionnaireConfigurationQuery(questionnaireConfigJobId, {
    enabled: isPastVacancyConfirm,
  });

  const refs = useMemo(() => ({ subject, message, setSubject, setMessage }), [subject, message]);

  const typeActivateRobinJrDialog = useMemo(() => {
    return operations.isResuming ? ActivateRobinJrDialogType.Reactivate : ActivateRobinJrDialogType.Activate;
  }, [operations.isResuming]);

  const handleStartPipeline = useCallback(
    async (options?: {
      attemptId?: number;
      questionnaire?: QuestionnaireRequirements;
      isAutoSendInterviewInviteEnabled?: boolean;
    }) => {
      if (!operations.selectedVacancy) {
        console.error('handleSendRequest: Missing selectedVacancy', {
          selectedVacancy: operations.selectedVacancy,
        });
        return;
      }

      const subject = refs.subject?.getTextContent({ replaceTokens: true }) || operations.contentRef.current?.subject;
      const message = refs.message?.serializeToHTML({ replaceTokens: true }) || operations.contentRef.current?.message;

      if (!isWhatsAppOnlyFlow && (!subject || !message)) {
        console.error('handleSendRequest: Missing subject or message content', {
          subject,
          message,
          subjectRef: refs.subject,
          messageRef: refs.message,
          contentRef: operations.contentRef.current,
        });
        return;
      }

      const whatsappMessage = isWhatsAppOnlyFlow
        ? buildWhatsappMessage({
            candidateFirstName: candidate.firstName ?? candidate.name?.split(' ')[0] ?? '',
            recruiterFirstName: currentUser?.first_name ?? '',
            companyName: currentUser?.tenant?.name ?? '',
            jobBoardName: candidate.activeJobBoard ? jobBoardLabel(candidate.activeJobBoard) : '',
            includeEmailNote: candidateHasEmail(candidate),
            ...buildRjWhatsappJobDetails(operations.improvedJob),
          })
        : undefined;

      operations.setIsLoading(true);

      try {
        if (isIndeedFlow) {
          const success = await sendIndeedOutreach({
            candidate,
            selectedVacancy: operations.selectedVacancy,
            subject: subject ?? '',
            message: message ?? '',
            attemptId: options?.attemptId,
            questionnaire: options?.questionnaire,
            isAutoSendInterviewInviteEnabled: options?.isAutoSendInterviewInviteEnabled,
            improvedJobPreview: operations.improvedJob?.job_preview ?? undefined,
          });

          if (!success) return;
        } else {
          await onRJComm({
            subject: subject || undefined,
            message: whatsappMessage ?? message ?? undefined,
            improved_job_id: operations.selectedVacancy.db_id ? Number(operations.selectedVacancy.db_id) : undefined,
            isAutoSendInterviewInviteEnabled: options?.isAutoSendInterviewInviteEnabled,
            interviewType: getNylasInterviewType(),
            isWhatsAppOnlyFlow,
          });
        }

        featureToggles.updateFeatureToggles();
        operations.refetchQueryBeforeClose();
        handleOutreachModalRequestClose();
      } catch (error) {
        console.error('handleSendRequest: Error occurred', error);

        createSnackbar(resolveIndeedRjSendErrorMessage(error, dictionary), { variant: 'danger' });

        modalNavigation.navigateToStep(modalNavigation.modalState);
      } finally {
        operations.setIsLoading(false);
      }
    },
    [
      refs.subject,
      refs.message,
      operations.selectedVacancy,
      operations.improvedJob,
      operations.contentRef,
      operations.setIsLoading,
      operations.refetchQueryBeforeClose,
      onRJComm,
      isIndeedFlow,
      isWhatsAppOnlyFlow,
      buildWhatsappMessage,
      currentUser,
      sendIndeedOutreach,
      candidate,
      featureToggles.updateFeatureToggles,
      handleOutreachModalRequestClose,
      modalNavigation.navigateToStep,
      dictionary,
      createSnackbar,
    ],
  );

  const handleConfirmSelectedVacancy = useCallback(async () => {
    if (!operations.selectedVacancy) return;

    if (operations.isResuming) {
      await operations.handleResumePipeline(handleOutreachModalRequestClose);
      return;
    }

    operations.setIsLoading(true);

    try {
      await operations.fetchImprovedJob();

      const jobId = operations.selectedVacancy.db_id ? Number(operations.selectedVacancy.db_id) : 0;
      await Promise.all([
        isWhatsAppOnlyFlow ? Promise.resolve() : operations.generateMessage(),
        jobId
          ? queryClient.fetchQuery({
              queryKey: getQuestionnaireConfigurationQueryKey(jobId),
              queryFn: () =>
                questionnaireConfigurationQueryFn({ queryKey: getQuestionnaireConfigurationQueryKey(jobId) }),
            })
          : Promise.resolve(),
      ]);

      const createdAttemptId = isIndeedFlow
        ? await createAttempt({ candidate, selectedVacancy: operations.selectedVacancy })
        : undefined;

      const nextStep = modalNavigation.determineNextStep(
        featureToggles.dontShowWhatsAppMessagePreview,
        featureToggles.dontShowMessagePreview,
        featureToggles.dontShowQuestionnairePreview,
      );

      modalNavigation.navigateToStep(nextStep);

      if (nextStep === ModalState.LOADING) {
        await handleStartPipeline({ attemptId: createdAttemptId });
      } else {
        operations.setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to generate message:', error);
      createSnackbar(dictionary.somethingWentWrong, { variant: 'danger' });
      const fallbackStep = initialVacancy ? ModalState.LOADING_GENERATED_MESSAGE : ModalState.ACTIVATE_ROBIN_JR;
      modalNavigation.navigateToStep(fallbackStep);
      operations.setIsLoading(false);
    }
  }, [
    operations.selectedVacancy,
    operations.isResuming,
    operations.handleResumePipeline,
    operations.setIsLoading,
    operations.generateMessage,
    operations.fetchImprovedJob,
    queryClient,
    modalNavigation.determineNextStep,
    modalNavigation.navigateToStep,
    featureToggles.dontShowMessagePreview,
    featureToggles.dontShowWhatsAppMessagePreview,
    featureToggles.dontShowQuestionnairePreview,
    isIndeedFlow,
    isWhatsAppOnlyFlow,
    createAttempt,
    candidate,
    handleOutreachModalRequestClose,
    handleStartPipeline,
    dictionary,
    createSnackbar,
    initialVacancy,
    isWaCommunicationAgentEnabled,
  ]);

  useSetupInitialVacancy(initialVacancy, { operations, handleConfirmSelectedVacancy });

  const handleConfirmMessage = useCallback(async () => {
    if (!operations.contentRef.current || !operations.selectedVacancy) return;

    featureToggles.updateFeatureToggles();

    const forceShowQuestionnaire = questionnaireConfig?.pending_review === true;

    const nextStep = forceShowQuestionnaire
      ? ModalState.QUESTIONNAIRE_PREVIEW
      : modalNavigation.determineNextStepFromCurrent(
          ModalState.GENERATED_MESSAGE,
          featureToggles.dontShowWhatsAppMessagePreview,
          featureToggles.dontShowMessagePreview,
          featureToggles.dontShowQuestionnairePreview,
        );

    if (operations.contentRef.current && refs.subject && refs.message) {
      const nextSubject = refs.subject.getTextContent({ replaceTokens: true });
      const nextMessage = refs.message.serializeToHTML({ replaceTokens: true });
      if (nextSubject != null) operations.contentRef.current.subject = nextSubject;
      if (nextMessage != null) operations.contentRef.current.message = nextMessage;
    }

    modalNavigation.navigateToStep(nextStep);

    if (nextStep === ModalState.LOADING) {
      await handleStartPipeline();
    }
  }, [
    operations.contentRef,
    operations.selectedVacancy,
    questionnaireConfig,
    featureToggles.updateFeatureToggles,
    featureToggles.dontShowMessagePreview,
    featureToggles.dontShowWhatsAppMessagePreview,
    featureToggles.dontShowQuestionnairePreview,
    modalNavigation.determineNextStepFromCurrent,
    modalNavigation.navigateToStep,
    handleStartPipeline,
    refs.subject,
    refs.message,
  ]);

  const handleConfirmWhatsappMessage = useCallback(async () => {
    if (!operations.selectedVacancy) return;

    featureToggles.updateFeatureToggles();

    try {
      if (!isWhatsAppOnlyFlow && !operations.contentRef.current) {
        await operations.generateMessage();
      }

      const nextStep = modalNavigation.determineNextStepFromCurrent(
        ModalState.WHATSAPP,
        featureToggles.dontShowWhatsAppMessagePreview,
        featureToggles.dontShowMessagePreview,
        featureToggles.dontShowQuestionnairePreview,
      );
      modalNavigation.navigateToStep(nextStep);

      if (nextStep === ModalState.LOADING) {
        await handleStartPipeline();
      }
    } catch (error) {
      console.error('Failed in WhatsApp confirmation:', error);
      createSnackbar(dictionary.somethingWentWrong, { variant: 'danger' });
      // Stay on WhatsApp screen on error so user can try again
      modalNavigation.navigateToStep(ModalState.WHATSAPP);
    }
  }, [
    operations.selectedVacancy,
    operations.contentRef,
    operations.generateMessage,
    isWhatsAppOnlyFlow,
    featureToggles.updateFeatureToggles,
    modalNavigation.determineNextStepFromCurrent,
    modalNavigation.navigateToStep,
    featureToggles.dontShowMessagePreview,
    featureToggles.dontShowWhatsAppMessagePreview,
    featureToggles.dontShowQuestionnairePreview,
    handleStartPipeline,
    dictionary,
    createSnackbar,
  ]);

  const handleConfirmQuestionnaire = useCallback(
    async (questionnaire?: CandidateRequirementsDTO | null, isAutoSendInterviewInviteEnabled = false) => {
      if (!operations.selectedVacancy) return;

      featureToggles.updateFeatureToggles();
      modalNavigation.navigateToStep(ModalState.LOADING);

      const jobId = operations.selectedVacancy.db_id ? Number(operations.selectedVacancy.db_id) : 0;
      const newRequirements = questionnaire
        ? candidateRequirementsToQuestionnaireRequirements(questionnaire)
        : undefined;

      if (isIndeedFlow) {
        await handleStartPipeline({
          questionnaire: newRequirements,
          isAutoSendInterviewInviteEnabled,
        });
        return;
      }

      if (jobId && newRequirements) {
        const hasChanged =
          !questionnaireConfig || !isEqual(questionnaireConfig.questionnaire_requirements, newRequirements);
        const mustClearPendingReview = questionnaireConfig?.pending_review === true;
        if (hasChanged || mustClearPendingReview) {
          await communicationClient.updateQuestionnaireConfiguration({
            job_id: jobId,
            questionnaire_requirements: newRequirements,
            ...(mustClearPendingReview && { pending_review: false }),
          });
          await queryClient.invalidateQueries({ queryKey: getQuestionnaireConfigurationQueryKey(jobId) });
        }
      }

      await handleStartPipeline({ isAutoSendInterviewInviteEnabled });
    },
    [
      operations.selectedVacancy,
      questionnaireConfig,
      isIndeedFlow,
      featureToggles.updateFeatureToggles,
      modalNavigation.navigateToStep,
      handleStartPipeline,
      queryClient,
    ],
  );

  // Only set vacancy from vacancyToSelect when initialVacancy is not provided
  // When initialVacancy is provided, it's already set via initial state in useOperations
  useEffect(() => {
    if (initialVacancy) return;

    if (!operations.rjActivityLogsQuery.isLoading) {
      operations.setSelectedVacancy(operations.vacancyToSelect);
    }
  }, [
    initialVacancy,
    operations.vacancyToSelect,
    operations.rjActivityLogsQuery.isLoading,
    operations.setSelectedVacancy,
  ]);

  // Auto-trigger enrichment for pre-selected unimproved vacancy, same as clicking from dropdown
  const autoEnrichedVacancyRef = useRef<string | null>(null);
  useEffect(() => {
    if (initialVacancy) return;
    if (operations.rjActivityLogsQuery.isLoading) return;

    const vacancy = operations.vacancyToSelect;
    if (!vacancy || vacancy.is_improved) return;
    if (autoEnrichedVacancyRef.current === vacancy.value) return;

    autoEnrichedVacancyRef.current = vacancy.value;
    operations.handleSelectVacancy(vacancy);
  }, [
    initialVacancy,
    operations.vacancyToSelect,
    operations.rjActivityLogsQuery.isLoading,
    operations.handleSelectVacancy,
  ]);

  useEffect(() => {
    if (refs.subject && refs.message && operations.contentRef.current) {
      refs.subject.importFromHTML(operations.contentRef.current.subject);
      refs.message.importFromHTML(operations.contentRef.current.message);
    }
  }, [refs.subject, refs.message, operations.contentRef]);

  return {
    refs,
    isResuming: operations.isResuming,
    selectedVacancy: operations.selectedVacancy,
    handleDialogClose: handleOutreachModalRequestClose,
    showLoadingProcessingVacancy: operations.showLoadingProcessingVacancy,
    isGeneratingMessage: operations.isGeneratingMessage,
    handleConfirmMessage,
    handleConfirmWhatsappMessage,
    handleConfirmQuestionnaire,
    handleSendRequest: handleStartPipeline,
    modalState: modalNavigation.modalState,
    setModalState: modalNavigation.setModalState,
    handleConfirmSelectedVacancy,
    isLoading: operations.isLoading,
    setSelectedAts: operations.handleSelectVacancy,
    typeActivateRobinJrDialog,
    dontShowMessagePreview: featureToggles.dontShowMessagePreview,
    setDontShowMessagePreview: featureToggles.setDontShowMessagePreview,
    dontShowWhatsAppMessagePreview: featureToggles.dontShowWhatsAppMessagePreview,
    setDontShowWhatsAppMessagePreview: featureToggles.setDontShowWhatsAppMessagePreview,
    dontShowQuestionnairePreview: featureToggles.dontShowQuestionnairePreview,
    setDontShowQuestionnairePreview: featureToggles.setDontShowQuestionnairePreview,
    showMessagePreviewCheckboxInActivate: !isWhatsAppOnlyFlow && !!featureToggles.profileDontShowGeneratedMessage,
    showWhatsAppPreviewCheckboxInActivate:
      !!featureToggles.profileDontShowWhatsappMessage &&
      isWaCommunicationAgentEnabled &&
      !!candidate?.contact_info?.phone,
    showQuestionnairePreviewCheckboxInActivate: !!featureToggles.profileDontShowQuestionnairePreview,
    improvedVacancy: operations.improvedJob,
    isWaCommunicationAgentEnabled,
    modalNavigation,
    goBack: modalNavigation.goBack,
    generateMessage: operations.generateMessage,
    selectedIndeedProject,
    setSelectedIndeedProject,
  };
};
